import { NAV_PATHS } from '@modules/common/constants';
import {
  useGetQuantifyCompareQuery,
  useGetQuantifyWhereClause,
  useIsAnomaliesTrendVisible,
} from '@modules/quantify/hooks';
import { FilterAltRounded, SettingsRounded } from '@mui/icons-material';
import { Box, Stack, Typography, alpha } from '@mui/material';
import { isEqual } from 'lodash';
import { ReactNode } from 'react';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useQuantifyStore } from '../store/quantifyStore';

export const useGetQuantifyReportStatus = (): ReactNode => {
  const visibleDataPoints = useQuantifyStore(
    ({ query }) => query.visibleDataPoints,
  );
  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.formulaQuery?.criteria,
  );
  const compareQuery = useGetQuantifyCompareQuery();
  const whereClause = useGetQuantifyWhereClause();

  const tableRows = useQuantifyStore(({ reportData }) =>
    compareQuery
      ? reportData.compare.tableRows
      : reportData?.quantify.tableRows,
  );
  const chartDataPoints = useQuantifyStore(({ reportData }) =>
    compareQuery
      ? reportData.compare.chartDataPoints
      : reportData?.quantify.chartDataPoints,
  );
  const isTableQueryInProgress = useQuantifyStore(
    ({ visual }) => visual.isTableQueryInProgress,
  );
  const isChartQueryInProgress = useQuantifyStore(
    ({ visual }) => visual.isChartQueryInProgress,
  );
  const groupBy = useQuantifyStore(({ query }) => query.groupByField);
  const isAnomaliesTrendVisible = useIsAnomaliesTrendVisible();

  if (!groupBy) {
    return 'Add Group by to proceed.';
  }

  if (
    (isTableQueryInProgress && !tableRows) ||
    (isChartQueryInProgress && !chartDataPoints)
  ) {
    return "We're cooking rich data for you. Please bear with us.";
  }

  const isFilterAEmpty = !whereClause || isEqual(whereClause, EmptyWhereClause);
  const isFilterBEmpty = compareQuery?.whereClause
    ? isEqual(compareQuery.whereClause, EmptyWhereClause)
    : false;

  if (compareQuery?.whereClause && (isFilterAEmpty || isFilterBEmpty)) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <FilterAltRounded />
        <Box>To compare, please add both Filters - A & B.</Box>
      </Stack>
    );
  }

  if (!measuredAsCriteria) {
    return (
      <Stack spacing={1}>
        <Typography variant="h6" color="text.primary">
          Hold on, partner! 🕵️‍♂️ You're missing the crucial{' '}
          <Box
            component="span"
            sx={{
              background: (theme) => alpha(theme.palette.primary.main, 0.3),
            }}
          >
            'Measured As Criteria'
          </Box>{' '}
          value.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          It's like trying to bake cookies without the secret ingredient – pure
          chaos in the kitchen!
        </Typography>
      </Stack>
    );
  }

  const isReportRoute = window.location.pathname.includes(
    `/${NAV_PATHS.REPORT}/`,
  );
  const reportDataPoints = isReportRoute ? chartDataPoints : tableRows;

  if (isAnomaliesTrendVisible && !reportDataPoints?.length) {
    return (
      <Stack spacing={1}>
        <Box>No Anomalies found.</Box>
        <Box>
          Try changing the Anomalies config by clicking on the{' '}
          <SettingsRounded
            fontSize="small"
            sx={{
              color: 'text.secondary',
            }}
          />{' '}
          icon.
        </Box>
        <Box>
          You can also widen the search duration, adjust the aggregation period,
          or remove filters (if any).
        </Box>
      </Stack>
    );
  }

  if (!reportDataPoints?.length) {
    return (
      <Stack spacing={1}>
        <Box>No results to show.</Box>
        <Box>
          Try widening the search duration or removing filters (if any).
        </Box>
      </Stack>
    );
  }

  if (!visibleDataPoints?.length) {
    return 'No data selected to view.';
  }
};
